/**
 * Team Members List Component
 * Task 5.3: Create Team Members List Component
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { TeamMemberCard } from './TeamMemberCard';
import { teamMemberService } from '@/lib/api/teamMember.service';
import { celebrationInvitationService } from '@/lib/api/celebration-invitation.service';
import { useAuth } from '@/hooks/useAuth';
import { ROLE_LABELS } from '@/types/teamMember.types';
import type { CelebrationInvitationSummary } from '@/types/celebration-invitation.types';
import type { TeamMemberWithUser } from '@/types/teamMember.types';

interface TeamMembersListProps {
  celebrationId: number;
  canEdit: boolean;
  onEdit: (member: TeamMemberWithUser) => void;
  onRemove: (member: TeamMemberWithUser) => void;
  refreshTrigger?: number;
}

export const TeamMembersList: React.FC<TeamMembersListProps> = ({
  celebrationId,
  canEdit,
  onEdit,
  onRemove,
  refreshTrigger
}) => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMemberWithUser[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<CelebrationInvitationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTeamMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, invites] = await Promise.all([
        teamMemberService.getTeamMembers(celebrationId),
        celebrationInvitationService.getCelebrationInvitations(celebrationId, 'team_member'),
      ]);
      setMembers(data);
      setPendingInvitations(invites);
    } catch (err: any) {
      console.error('Error loading team members:', err);
      setError(err.response?.data?.message || err.response?.data?.error || t('team.list.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [celebrationId]);

  useEffect(() => {
    loadTeamMembers();
  }, [loadTeamMembers, refreshTrigger]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (members.length === 0 && pendingInvitations.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{t('team.list.empty')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {pendingInvitations.length > 0 && (
        <div className="space-y-3">
          {pendingInvitations.map((invitation) => (
            <Card key={`invite-${invitation.id}`} className="border-dashed border-amber-300 bg-amber-50/60">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg text-slate-900">{invitation.invitee_name}</h3>
                      <span className="px-2 py-1 text-xs font-medium rounded-full border bg-amber-100 text-amber-800 border-amber-200">
                        {t('team.list.invited')}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600">{invitation.invitee_email}</div>
                    <div className="text-sm text-slate-600 mt-1">
                      {t('team.list.role')}: {invitation.team_role ? ROLE_LABELS[invitation.team_role] : t('team.list.teamMemberFallback')}
                    </div>
                  </div>
                  <div className="text-xs text-amber-700">{t('team.list.awaitingResponse')}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {members.map((member) => (
        <TeamMemberCard
          key={member.id}
          member={member}
          canEdit={canEdit}
          canRemove={!member.is_celebration_creator && member.user_id !== user?.id}
          removeDisabledReason={
            member.is_celebration_creator
              ? t('team.list.creatorCannotBeRemoved')
              : t('team.list.cannotRemoveSelf')
          }
          onEdit={onEdit}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
};
